import { useEffect, useMemo, useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import "./AuthGate.css";

function AuthGate() {
  const { t } = useTranslation();
  const { signIn } = useAuthActions();
  const { search } = useLocation();
  const redirectTo = `${window.location.origin}/projects`;
  const initialMode = useMemo(() => {
    const modeParam = new URLSearchParams(search).get("mode");
    return modeParam === "signup" ? "signup" : "signin";
  }, [search]);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.classList.add("auth-scroll");
    return () => document.body.classList.remove("auth-scroll");
  }, []);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError("");
    try {
      await signIn("google", { redirectTo });
    } catch (err) {
      setError(err?.message || t('auth.unableToStartGoogle'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAuth = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      if (mode === "signup" && password.length < 6) {
        setError(t('auth.passwordHint'));
        setIsLoading(false);
        return;
      }
      const flow = mode === "signup" ? "signUp" : "signIn";
      await signIn("password", { flow, email, password, redirectTo });
    } catch (err) {
      const message = err?.message || "";
      if (message.includes("Invalid credentials") || message.includes("invalid credentials")) {
        setError(mode === "signin"
          ? t('auth.invalidEmailOrPassword')
          : t('auth.accountAlreadyExists'));
      } else if (message.includes("Could not find") || message.includes("No account")) {
        setError(t('auth.noAccountFound'));
      } else if (message.includes("already exists") || message.includes("already registered") || message.includes("already in use")) {
        setError(t('auth.accountAlreadyExists'));
      } else if (message.includes("Invalid password") || message.includes("password")) {
        setError(t('auth.passwordHint'));
      } else if (message.includes("JWT_PRIVATE_KEY")) {
        setError(t('auth.missingJwtKey'));
      } else if (message.includes("SITE_URL")) {
        setError(t('auth.missingSiteUrl'));
      } else if (message.includes("GOOGLE_CLIENT") || message.includes("AUTH_GOOGLE")) {
        setError(t('auth.missingGoogleClient'));
      } else {
        // Never show raw Convex errors to users
        console.error("Auth error:", message);
        setError(mode === "signin"
          ? t('auth.invalidEmailOrPassword')
          : t('auth.unableToSignIn'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-gate">
      <div className="auth-gate-card">
        <div className="auth-gate-logo">✒️</div>
        <h1>{t('auth.welcomeTitle')}</h1>
        <p>
          {t('auth.welcomeSubtitle')}
        </p>
        <button
          className="btn btn-primary auth-gate-button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          {isLoading ? t('auth.connecting') : t('auth.continueWithGoogle')}
        </button>

        <div className="auth-divider">
          <span>{t('common.or')}</span>
        </div>

        <div className="auth-toggle">
          <button
            className={`btn btn-ghost ${mode === "signin" ? "active" : ""}`}
            onClick={() => setMode("signin")}
            disabled={isLoading}
          >
            {t('auth.signIn')}
          </button>
          <button
            className={`btn btn-ghost ${mode === "signup" ? "active" : ""}`}
            onClick={() => setMode("signup")}
            disabled={isLoading}
          >
            {t('auth.createAccount')}
          </button>
        </div>

        <form className="auth-form" onSubmit={handleEmailAuth}>
          <label>
            {t('auth.email')}
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label>
            {t('auth.password')}
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
            />
          </label>
          {mode === "signup" && (
            <div className="auth-hint">{t('auth.passwordHint')}</div>
          )}
          {error && <div className="auth-error">{error}</div>}
          <button className="btn btn-primary auth-gate-button" type="submit" disabled={isLoading}>
            {isLoading
              ? t('auth.submitting')
              : mode === "signup"
                ? t('auth.createAccountWithEmail')
                : t('auth.continueWithEmail')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AuthGate;
